import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation, useApolloClient } from '@apollo/client';
import { useDocumentState } from '../../../../../../contexts/DocumentContext';
import { documentStateFromResponse } from '../../../../../../utils/document-wizard-utils/document-state-from-response';
import PropdocsModal from '../../../../../modal/PropdocsModal';
import { unlockDocumentAfterApproval } from '../../../../../../api/mutations';
import { getDocumentById as getDocumentByIdQuery } from '../../../../../../utils/api/get-document-by-id';
import { useNotifications } from '../../../../../../notifications';
import {
  ModalActionDescription,
  ModalActionTitle,
  UnlockDocumentMessage,
} from '../../../../../document-table/DocumentTableConstants';

interface Props {
  onHide: () => void;
  show: boolean;
  documentId: string;
  teamId: string;
}

const CancelApprovalRequestModal = ({ onHide, show, documentId, teamId }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const apolloClient = useApolloClient();
  const { updateDocumentState } = useDocumentState();

  const [unlockDocumentAfterApprovalMutation] = useMutation(unlockDocumentAfterApproval);

  const [, { error: notifyError, success: notifySuccess }] = useNotifications();

  const handleCancelApprovalRequest = useCallback(async () => {
    setIsLoading(true);

    try {
      // 1. First perform the unlock mutation
      await unlockDocumentAfterApprovalMutation({
        variables: {
          documentId,
          teamId,
        },
      });

      // 2. Clear Apollo Client cache for document-related queries
      apolloClient.cache.evict({
        id: `Document:${documentId}`,
        fieldName: 'documentApprovalRequest',
      });
      apolloClient.cache.evict({
        id: `Document:${documentId}`,
        fieldName: 'isEditable',
      });
      apolloClient.cache.gc(); // Garbage collect orphaned cache entries

      // 3. Refetch fresh document data and update context
      const response = await getDocumentByIdQuery(documentId)(apolloClient);
      updateDocumentState(documentStateFromResponse(response));

      // 4. Additional cache cleanup for editor-related operations
      apolloClient.cache.evict({ fieldName: 'syncDocumentSuggestions' });

      // Force refetch of all active queries to ensure fresh data
      await apolloClient.refetchQueries({
        include: 'active',
      });

      apolloClient.cache.gc();

      // 5. Close the modal first
      onHide();

      // 6. Show success notification with delay to avoid render conflicts
      setTimeout(() => {
        notifySuccess({
          message: { id: 'document.unlock-action.success', defaultMessage: UnlockDocumentMessage.SUCCESS },
        });
      }, 100);
    } catch (error) {
      notifyError({ message: error });
    } finally {
      setIsLoading(false);
    }
  }, [
    unlockDocumentAfterApprovalMutation,
    notifyError,
    notifySuccess,
    documentId,
    teamId,
    apolloClient,
    updateDocumentState,
    onHide,
  ]);

  return (
    <PropdocsModal
      size="lg"
      show={show}
      setShow={onHide}
      title={
        <FormattedMessage
          id="document-wizard-modal.handle_internal_approval"
          defaultMessage={ModalActionTitle.UNLOCK_DOCUMENT}
        />
      }
    >
      <>
        <div style={{ marginBottom: '1.5rem' }}>{ModalActionDescription.UNLOCK_DOCUMENT}</div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
          <button type="button" className="btn btn-outline-secondary" onClick={onHide}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancelApprovalRequest}
            disabled={isLoading}
          >
            {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
            Unlock Document
          </button>
        </div>
      </>
    </PropdocsModal>
  );
};

export default CancelApprovalRequestModal;
